import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { photosAvailable, photosRequired } from './Question';
import { useImageStore } from '../../lib/stores/image'
import { toast } from 'react-toastify';
import { CloudUploadIcon, SaveIcon, XIcon } from 'lucide-react'
import PropTypes from 'prop-types';
import FormError from '../partials/FormError';
import __ from '../../helpers/translation';

/**
 * @typedef {Object} PropTypes
 * @property {Object} current - The current object, required.
 * @property {string} [errors] - The string containing error messages.
 * @property {Function} [continue] - The function to continue the process.
 */
const propTypes = {
  current: PropTypes.object.isRequired,
  errors: PropTypes.string,
  continue: PropTypes.func,
};

/**
 * UploadPhotoModal component renders a modal for uploading photos associated with a specific question.
 * It allows users to select and preview images, and handles the upload process.
 * 
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.current - The current question object.
 * @param {Object} props.errors - Errors related to the upload process.
 * @param {Function} props.continue - Function to proceed to the next question.
 * @param {Object} props.previewPhoto - Object containing the preview photo state.
 * 
 * @returns {JSX.Element} The rendered UploadPhotoModal component.
 */
const UploadPhotoModal = (props) => {

  useEffect(() => {
    console.info('UploadPhotoModal mounted with props:', props);
  }, []);
  
  /**
   * The reference to the file input element. This
   * is used to trigger the file selection dialog and
   * to retrieve the selected file(s).
   * 
   * @type {React.RefObject<HTMLInputElement>}
   */
  const fileInputRef = useRef(null);

  const { 
    images, 
    addImage,
    pushImageToBucket,
    removeImage,
    isUploading, 
  } = useImageStore();

  /**
   * Shows the file selection dialog.
   * 
   * @returns {void}
   */
  const showFileSelection = () => {
    fileInputRef.current.click();
  }

  /**
   * Only retrieve the images that are associated with the current question. These can be displayed in the modal.
   */
  const uploadedImages = useMemo(() => {
    const storedImages = Array.from(images.values())
    return storedImages.filter(image => image.answerId === props.current.id);
  }, [images, props.current])

  /**
   * Handles the file input change event.
   * 
   * @param {File} file - The selected file.
   * @returns {void}
   */
  const onFileInputChange = useCallback((file) => {
    
    // 1. Get the answer ID
    const answerId = props?.current?.id;

    // 2. Add the image to the store
    addImage(file, answerId)
      .then(async (image) => {
        console.info('Image added successfully:', image.name);

        const { success, error } = await pushImageToBucket(image.hash);

        if(success) {
          console.info('Image pushed to bucket:', image.name);
        }
        else if(error) {
          console.error('Error pushing image to bucket:', error);
        }
      })
      .catch((err) => {
        console.error('Error adding image:', err);
      })

  }, [fileInputRef, props.current]);

  return (
    <>

      {/* Photo modal */}
      <div 
        className="modal fade" 
        id="uploadPhotoModal" 
        tabIndex="-1" 
        role="dialog" 
        aria-labelledby="uploadPhotoModal" 
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content p-3 p-sm-5">

              {/* Modal header */}
              <div className="modal-header pb-0">
                <h1 className="modal-title fw-600" id="exampleModalLongTitle">
                  {props.current && photosRequired(props.current) 
                    ? __('app.questions.upload_photo_popup_title') 
                    : __('app.questions.upload_photo_popup_title_optional')
                  }
                </h1>
                <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              
              {/* Modal body */}
              <div className="modal-body md-question">

                {/* Render the question remark */}
                {props?.current?.question_data?.remark && (
                  <div dangerouslySetInnerHTML={{ __html: props.current.question_data.remark.replace(/(<? *script)/gi, 'illegalscript')}}></div>
                )}

                {/* Show errors */}
                {props.errors && <FormError>{props.errors}</FormError>}

                {/* Show loading spinner */}
                {isUploading && (
                  <div className="row">
                    <div className="is-loading">
                      <i className="far fa-spinner fa-pulse"></i>
                    </div>
                  </div>
                )}

                {/* Photo preview */}
                <div className="tw-grid tw-grid-cols-4 tw-gap-4 tw-mt-6 tw-mb-12 tw-isolate">
                  {uploadedImages.map(image => (
                    <div 
                      key={image.hash}
                      style={{ backgroundImage: `url(${image.dataUri})` }}
                      className="tw-bg-no-repeat tw-bg-cover tw-aspect-square tw-rounded-xl tw-transition-all tw-duration-300 tw-relative"
                    >
                      <button 
                        onClick={() => removeImage(image.hash)}
                        className="tw-size-7 tw-flex tw-items-center tw-justify-center tw-absolute -tw-top-2 -tw-right-2 tw-bg-red-600 tw-rounded-md active:tw-size-6 tw-transition-all tw-duration-150"
                      >
                        <XIcon className="tw-size-6 tw-text-white"/>
                      </button>
                      {image.pushedToBucket && (
                        <div className="tw-size-7 tw-flex tw-items-center tw-justify-center tw-absolute -tw-right-2 tw-top-7 tw-bg-green-700 tw-rounded-md">
                          <CloudUploadIcon className='tw-size-5 tw-text-white'/>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Upload image button */}
                <button 
                  disabled={props.previewPhoto && props.previewPhoto.isLoading}
                  type="button" 
                  className="btn btn-dark d-block mt-3 w-100 beo-upload pic"
                  onClick={showFileSelection}
                >
                  {uploadedImages.length > 0
                    ? __('app.questions.upload_photo_popup_button_more')
                    : __('app.questions.upload_photo_popup_button')
                  }

                  {isUploading && (
                    <i className="far fa-circle-notch fa-spin ml-3"></i>
                  )}
                </button>

                {/* Continue button */}
                <button 
                  type="button" 
                  disabled={photosRequired(props.current) && uploadedImages.length < 1}
                  className={`btn btn-light mt-3 w-100 beo-next toggled ${props.continue ? '' : 'd-none'}`}
                  { ... props.continue ? {onClick: () => props.continue()} : {} }
                >
                  { __('app.questions.upload_photo_popup_next_question') }
                </button>

              </div>
          </div>
        </div>
      </div>

      {/* Hidden input */}
      <div className="d-none">
        <div className="beo-file-uploads">
          <input 
            onChange={(e) => onFileInputChange(e.target.files[0])} 
            ref={fileInputRef} 
            type="file" 
            name="files[]" 
            id="selected-file"
            className="beo-file-upload" 
            accept="image/*"
            capture="capture" 
          />
        </div>
      </div>
    </>
  );
  
}

UploadPhotoModal.propTypes = propTypes;

export default UploadPhotoModal;